// Enderecos.jsx
import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";

function cidadesUnicas(enderecos = []) {
  const set = new Set();
  enderecos.forEach((e) => {
    const c = (e?.cidade || "").toString().trim().toLowerCase();
    const uf = (e?.uf || "").toString().trim().toUpperCase();
    if (c) set.add(`${c}|${uf}`);
  });
  return Array.from(set).map((x) => {
    const [c, uf] = x.split("|");
    return { cidade: c, uf };
  });
}

const Enderecos = ({ enderecos = [] }) => {
  const [expandido, setExpandido] = useState(false);

  const LIMITE = 5;
  const total = enderecos.length;
  const restantes = Math.max(total - LIMITE, 0);
  const cidades = cidadesUnicas(enderecos);

  const listaExibida = useMemo(
    () => (expandido ? enderecos : enderecos.slice(0, LIMITE)),
    [expandido, enderecos]
  );

  return (
    <>
      {/* Resumo */}
      <div className="flex flex-wrap gap-2 mb-4 mt-3">
        {total > 0 && (
          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
            <span>Quantidade total:</span>
            <span>{total}</span>
          </div>
        )}
        {cidades.length > 0 && (
          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
            <span>Cidades únicas:</span>
            <span>{cidades.length}</span>
          </div>
        )}
      </div>

      {/* Lista */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="grid grid-cols-6 gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
            <div>Endereço</div>
            <div className="text-center">Número</div>
            <div className="text-center">Complemento</div>
            <div className="text-center">Bairro</div>
            <div className="text-center">Cidade</div>
            <div className="text-center">CEP</div>
          </div>
        </div>

        <div
          className={`transition-all duration-300 ease-out ${
            expandido ? "max-h-[9999px]" : "max-h-auto"
          } overflow-hidden`}
        >
          {listaExibida.map((endereco, index) => (
            <div
              key={index}
              className={`grid grid-cols-6 items-center gap-2 px-4 py-3 text-sm ${
                index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
              }`}
            >
              <div className="text-slate-900">
                {endereco?.logradouro || "-"}
              </div>
              <div className="text-center text-slate-700">
                {endereco?.numero || "-"}
              </div>
              <div className="text-center text-slate-700">
                {endereco?.complemento || "-"}
              </div>
              <div className="text-center text-slate-700">
                {endereco?.bairro || "-"}
              </div>
              <div className="text-center text-slate-700">
                {endereco?.cidade || "-"}
                {endereco?.uf ? `/${endereco.uf}` : ""}
              </div>
              <div className="text-center text-slate-700">
                {endereco?.cep || "-"}
              </div>
            </div>
          ))}
        </div>

        {!expandido && total > LIMITE && (
          <div className="pointer-events-none absolute inset-x-0 bottom-[52px] h-16 bg-gradient-to-t from-white to-white/0" />
        )}

        {total > LIMITE && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              {expandido ? (
                <>
                  <HiChevronUp className="text-lg" />
                  Ver menos
                </>
              ) : (
                <>
                  <HiChevronDown className="text-lg" />
                  Ver mais
                </>
              )}
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Badge color="gray">
                Exibindo {Math.min(LIMITE, total)} de {total}
              </Badge>
              {!expandido && restantes > 0 && (
                <span className="text-slate-500">
                  (+{restantes} restantes)
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {!expandido && total > LIMITE && (
        <div className="mt-3 text-center text-sm text-slate-500">
          Exibindo as últimas {LIMITE} ocorrências.
        </div>
      )}
    </>
  );
};

export default Enderecos;
